/**
 * TerugNaarJouwKrachtSession — 3-minute strength & boundary session.
 * Cycles through: Intro → Zinnen → Ademhaling → Afsluiting
 */
import React, { useState, useEffect, useRef } from 'react';
import { C, _T } from '../../../shared/theme';
import { s_GB } from '../../../shared/styles';

const COLOR = '#C8A84B';   // warm gold — kracht & stevigheid
const TOTAL = 180;         // seconds

const PHASES = [
  { id: 0, start: 0,   end: 28  },  // Intro + Stap 1
  { id: 1, start: 28,  end: 108 },  // Stap 2 — zinnen
  { id: 2, start: 108, end: 162 },  // Stap 3 — ademhaling
  { id: 3, start: 162, end: 180 },  // Afsluiting
];

const SENTENCES = [
  'Ik heb mijn best gedaan.',
  'Ik stel nu een grens.',
  'Dat is oké.',
];

// ── Breathing circle (5s in · 5s out — coherent / hart-coherentie) ───────────
function StrengthBreathCircle({ active }) {
  const [bs, setBs] = useState({ phase: 'in', progress: 0, secs: 5 });
  const ref = useRef(null);

  useEffect(() => {
    if (!active) { clearInterval(ref.current); return; }
    const DUR = { in: 5000, out: 5000 };
    let phase = 'in';
    let phaseStart = Date.now();

    ref.current = setInterval(() => {
      const now     = Date.now();
      const elapsed = now - phaseStart;
      if (elapsed >= DUR[phase]) {
        phase      = phase === 'in' ? 'out' : 'in';
        phaseStart = now;
      }
      const progress = Math.min(1, (now - phaseStart) / DUR[phase]);
      const secsLeft = Math.ceil((DUR[phase] - (now - phaseStart)) / 1000);
      setBs({ phase, progress, secs: Math.max(0, secsLeft) });
    }, 100);

    return () => clearInterval(ref.current);
  }, [active]);

  const scale = bs.phase === 'in' ? 0.45 + 0.55 * bs.progress : 1.0 - 0.55 * bs.progress;
  const BASE  = 80;
  const r     = BASE * scale;
  const glow  = Math.round(scale * 40);
  const label = bs.phase === 'in' ? 'Adem in — 5 sec' : 'Adem uit — 5 sec';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', margin: '16px 0 8px' }}>
      <div style={{ position: 'relative', width: BASE * 2 + 60, height: BASE * 2 + 60, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {/* ambient rings */}
        <div style={{ position: 'absolute', width: BASE * 2 + 60, height: BASE * 2 + 60, borderRadius: '50%', border: `1px solid ${COLOR}18` }} />
        <div style={{ position: 'absolute', width: BASE * 2 + 30, height: BASE * 2 + 30, borderRadius: '50%', border: `1px solid ${COLOR}10` }} />
        {/* main circle */}
        <div style={{
          width: r * 2, height: r * 2, borderRadius: '50%',
          background: `radial-gradient(circle, ${COLOR}30 0%, ${COLOR}08 70%)`,
          border: `2px solid ${COLOR}${Math.round(scale * 99).toString(16).padStart(2, '0')}`,
          boxShadow: `0 0 ${glow}px ${COLOR}40, 0 0 ${glow * 2}px ${COLOR}15`,
          transition: 'width .15s ease-out, height .15s ease-out, box-shadow .15s ease',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{ color: COLOR, fontSize: Math.max(10, Math.round(scale * 13)), fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', textAlign: 'center', padding: '0 6px', lineHeight: 1.3 }}>
            {label}
          </div>
          {bs.secs > 0 && (
            <div style={{ color: '#fff', fontSize: Math.max(16, Math.round(scale * 26)), fontWeight: 700, fontFamily: 'Inter,sans-serif', marginTop: 4 }}>
              {bs.secs}
            </div>
          )}
        </div>
      </div>
      <div style={{ color: C.silver, fontSize: 11, marginTop: 4 }}>Hart-coherentie · 5 in · 5 uit</div>
    </div>
  );
}

// ── Rotating sentence with fadeUp ────────────────────────────────────────────
function RotatingSentence({ sentences, intervalSecs, active, elapsed }) {
  const idx    = active ? Math.floor(elapsed / intervalSecs) % sentences.length : 0;
  const animKey = active ? Math.floor(elapsed / intervalSecs) : 0;

  return (
    <div style={{ minHeight: 80, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '8px 0' }}>
      <div
        key={animKey}
        className="fadeUp"
        style={{
          fontFamily: 'Georgia,serif',
          fontSize: 20,
          color: '#fff',
          textAlign: 'center',
          lineHeight: 1.5,
          padding: '0 12px',
          borderLeft: `3px solid ${COLOR}`,
          paddingLeft: 16,
        }}
      >
        "{sentences[idx]}"
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function TerugNaarJouwKrachtSession({ lang, onFinish }) {
  const nl = lang === 'nl';
  const [secsLeft, setSecsLeft] = useState(TOTAL);
  const [running, setRunning]   = useState(true);
  const [done, setDone]         = useState(false);
  const timerRef = useRef(null);

  const elapsed  = TOTAL - secsLeft;
  const phase    = PHASES.findIndex(p => elapsed >= p.start && elapsed < p.end);
  const curPhase = phase === -1 ? 3 : phase;

  // Seconds elapsed within phase 1 (sentences phase)
  const sentenceElapsed = curPhase === 1 ? elapsed - PHASES[1].start : 0;

  // Countdown timer
  useEffect(() => {
    if (!running) return;
    timerRef.current = setInterval(() => {
      setSecsLeft(s => {
        if (s <= 1) { clearInterval(timerRef.current); setDone(true); return 0; }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [running]);

  const saveAndFinish = () => {
    const entry = {
      id:    Date.now(),
      type:  'terug_naar_kracht',
      label: nl ? 'Terug naar jouw kracht' : 'Back to your strength',
      icon:  '💛',
      color: COLOR,
      date:  new Date().toLocaleDateString(nl ? 'nl-NL' : 'en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }),
    };
    try {
      const prev = JSON.parse(localStorage.getItem('sl_saved_sessions') || '[]');
      prev.unshift(entry);
      if (prev.length > 50) prev.length = 50;
      localStorage.setItem('sl_saved_sessions', JSON.stringify(prev));
    } catch {}
    onFinish({ feelings: ['Steviger', 'Grens gesteld'], aiComment: null });
  };

  const mins = Math.floor(secsLeft / 60);
  const secs = secsLeft % 60;
  const pct  = ((TOTAL - secsLeft) / TOTAL) * 100;

  // ── Done screen ─────────────────────────────────────────────────────────────
  if (done || secsLeft === 0) {
    return (
      <div style={{ padding: '24px 0', textAlign: 'center' }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>💛</div>
        <div style={{ fontFamily: 'Georgia,serif', fontSize: 20, color: '#fff', marginBottom: 8 }}>
          {nl ? 'Sessie voltooid' : 'Session complete'}
        </div>
        <div style={{ color: COLOR, fontSize: 15, fontStyle: 'italic', marginBottom: 24, padding: '0 20px' }}>
          "Ik heb mijn best gedaan. Dat is oké."
        </div>
        <button onClick={saveAndFinish} style={s_GB({ fontSize: 14, padding: '12px 28px' })}>
          {nl ? 'Opslaan & afsluiten' : 'Save & finish'}
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Timer + progress bar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <div style={{ flex: 1, height: 4, background: 'rgba(255,255,255,.08)', borderRadius: 4 }}>
          <div style={{ height: '100%', width: `${pct}%`, background: `linear-gradient(90deg,${COLOR},#e8c96b)`, borderRadius: 4, transition: 'width 1s linear' }} />
        </div>
        <div style={{ color: C.silver, fontSize: 12, fontFamily: 'monospace', minWidth: 36 }}>
          {mins}:{String(secs).padStart(2, '0')}
        </div>
        <button onClick={() => setRunning(r => !r)} style={{ background: 'none', border: `1px solid rgba(255,255,255,.12)`, borderRadius: 8, color: C.silver, cursor: 'pointer', fontSize: 11, padding: '4px 10px' }}>
          {running ? '⏸' : '▶'}
        </button>
      </div>

      {/* Phase 0 — Intro + Stap 1 */}
      {curPhase === 0 && (
        <div className="fadeUp" style={{ background: `${COLOR}10`, border: `1px solid ${COLOR}33`, borderRadius: 16, padding: '22px 20px' }}>
          <div style={{ color: COLOR, fontSize: 11, fontWeight: 700, letterSpacing: 2, marginBottom: 10 }}>
            INTRODUCTIE
          </div>
          <div style={{ fontFamily: 'Georgia,serif', fontSize: 17, color: '#fff', marginBottom: 14, lineHeight: 1.5 }}>
            Als je begint te twijfelen of schuld voelt
          </div>
          <div style={{ background: 'rgba(0,0,0,.25)', borderRadius: 12, padding: '16px 18px', marginBottom: 14 }}>
            <div style={{ color: COLOR, fontSize: 11, fontWeight: 700, letterSpacing: 2, marginBottom: 10 }}>
              STAP 1 — RECHTOP ZITTEN / STAAN
            </div>
            <div style={{ color: '#e2e8f0', fontSize: 14, lineHeight: 1.7 }}>
              Zet je voeten stevig op de grond.<br />
              Recht je rug. Hef je hoofd op.<br />
              Voel de grond onder je.
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
            {['twijfel', 'schuldgevoel', 'niet goed genoeg'].map(t => (
              <span key={t} style={{ background: `${COLOR}18`, border: `1px solid ${COLOR}33`, borderRadius: 20, color: COLOR, fontSize: 11, padding: '3px 10px' }}>{t}</span>
            ))}
          </div>
        </div>
      )}

      {/* Phase 1 — Stap 2: zinnen */}
      {curPhase === 1 && (
        <div className="fadeUp" style={{ background: `${COLOR}10`, border: `1px solid ${COLOR}33`, borderRadius: 16, padding: '22px 20px' }}>
          <div style={{ color: COLOR, fontSize: 11, fontWeight: 700, letterSpacing: 2, marginBottom: 10 }}>
            STAP 2 — ZEG DIT (HARDOP OF IN JE HOOFD)
          </div>
          <div style={{ color: C.silver, fontSize: 12, marginBottom: 16 }}>
            Laat elke zin écht binnen komen. Herhaal zo vaak als nodig.
          </div>
          <RotatingSentence
            sentences={SENTENCES}
            intervalSecs={8}
            active={running}
            elapsed={sentenceElapsed}
          />
          {/* Sentence dots indicator */}
          <div style={{ display: 'flex', justifyContent: 'center', gap: 6, marginTop: 16 }}>
            {SENTENCES.map((_, i) => {
              const activeIdx = running ? Math.floor(sentenceElapsed / 8) % SENTENCES.length : 0;
              return (
                <div key={i} style={{
                  width: 6, height: 6, borderRadius: '50%',
                  background: i === activeIdx ? COLOR : `${COLOR}33`,
                  transition: 'background 0.4s',
                }} />
              );
            })}
          </div>
          <div style={{ color: C.silver, fontSize: 11, textAlign: 'center', marginTop: 12 }}>
            Spreek vanuit jouw kern — niet vanuit angst.
          </div>
        </div>
      )}

      {/* Phase 2 — Stap 3: ademhaling */}
      {curPhase === 2 && (
        <div className="fadeUp" style={{ background: `${COLOR}10`, border: `1px solid ${COLOR}33`, borderRadius: 16, padding: '22px 20px' }}>
          <div style={{ color: COLOR, fontSize: 11, fontWeight: 700, letterSpacing: 2, marginBottom: 10 }}>
            STAP 3 — ADEM DIEP IN EN LANGZAAM UIT
          </div>
          <div style={{ fontFamily: 'Georgia,serif', fontSize: 16, color: '#fff', marginBottom: 4 }}>
            Zet je hand op je borst
          </div>
          <div style={{ color: C.silver, fontSize: 12, marginBottom: 4 }}>
            Hart-coherentie ademhaling — brengt rust en kracht tegelijk.
          </div>
          <StrengthBreathCircle active={running && curPhase === 2} />
        </div>
      )}

      {/* Phase 3 — Afsluiting */}
      {curPhase === 3 && (
        <div className="fadeUp" style={{ background: `${COLOR}10`, border: `1px solid ${COLOR}33`, borderRadius: 16, padding: '32px 20px', textAlign: 'center' }}>
          <div style={{ color: COLOR, fontSize: 11, fontWeight: 700, letterSpacing: 2, marginBottom: 16 }}>
            AFSLUITING
          </div>
          <div style={{ fontSize: 40, marginBottom: 16 }}>💛</div>
          <div style={{ fontFamily: 'Georgia,serif', fontSize: 18, color: '#fff', lineHeight: 1.7, fontStyle: 'italic', padding: '0 16px', marginBottom: 16 }}>
            "Voel en maak stevigheid<br />in je lichaam."
          </div>
          <div style={{ color: C.silver, fontSize: 12, marginTop: 4 }}>
            Druk je voeten in de grond. Voel je eigen gewicht.<br />
            Jij staat hier. Jij beslist.
          </div>
        </div>
      )}

      {/* Skip to finish early */}
      <div style={{ textAlign: 'center', marginTop: 20 }}>
        <button onClick={() => { clearInterval(timerRef.current); setDone(true); }}
          style={{ background: 'none', border: 'none', color: C.silver, fontSize: 11, cursor: 'pointer', textDecoration: 'underline' }}>
          {nl ? 'Sessie afsluiten' : 'End session'}
        </button>
      </div>
    </div>
  );
}
